/**
 * Import Service
 *
 * Handles importing and validating agents, teams, and workflows from JSON
 */
import type { AgentCreate, AgentTeamCreate, WorkflowCreate } from '@/types'

export interface ImportResult<T> {
  success: boolean
  data?: T[]
  errors: ImportError[]
  warnings: string[]
  imported: number
  skipped: number
}

export interface ImportError {
  index: number
  field?: string
  message: string
}

export interface ImportOptions {
  validateOnly?: boolean
  skipInvalid?: boolean
  overwriteExisting?: boolean
}

class ImportService {
  /**
   * Parse JSON file
   */
  async parseJSONFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const text = e.target?.result as string
          const data = JSON.parse(text)
          resolve(data)
        } catch (error) {
          reject(new Error('Invalid JSON format'))
        }
      }

      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  /**
   * Import agents from JSON
   */
  async importAgents(
    data: any,
    options: ImportOptions = {}
  ): Promise<ImportResult<AgentCreate>> {
    const result: ImportResult<AgentCreate> = {
      success: false,
      data: [],
      errors: [],
      warnings: [],
      imported: 0,
      skipped: 0,
    }

    try {
      // Handle different JSON formats
      let agents: any[] = []

      if (Array.isArray(data)) {
        agents = data
      } else if (data.data && Array.isArray(data.data)) {
        agents = data.data
      } else if (data.agents && Array.isArray(data.agents)) {
        agents = data.agents
      } else {
        result.errors.push({
          index: -1,
          message: 'Invalid import format. Expected array of agents or object with "data" or "agents" field.',
        })
        return result
      }

      // Validate and transform each agent
      agents.forEach((agent, index) => {
        const validation = this.validateAgent(agent, index)

        if (!validation.valid) {
          result.errors.push(...validation.errors)
          result.skipped++

          if (!options.skipInvalid) {
            return
          }
        } else {
          const agentCreate: AgentCreate = {
            name: agent.name,
            description: agent.description,
            framework: agent.framework,
            model: agent.model,
            system_prompt: agent.system_prompt,
            temperature: agent.temperature,
            max_tokens: agent.max_tokens,
            tools: agent.tools,
            configuration: agent.configuration,
            status: agent.status || 'draft',
          }

          result.data!.push(agentCreate)
          result.imported++
        }

        if (validation.warnings.length > 0) {
          result.warnings.push(...validation.warnings)
        }
      })

      result.success = result.errors.length === 0 || options.skipInvalid === true

      return result
    } catch (error: any) {
      result.errors.push({
        index: -1,
        message: error.message || 'Import failed',
      })
      return result
    }
  }

  /**
   * Import teams from JSON
   */
  async importTeams(
    data: any,
    options: ImportOptions = {}
  ): Promise<ImportResult<AgentTeamCreate>> {
    const result: ImportResult<AgentTeamCreate> = {
      success: false,
      data: [],
      errors: [],
      warnings: [],
      imported: 0,
      skipped: 0,
    }

    try {
      let teams: any[] = []

      if (Array.isArray(data)) {
        teams = data
      } else if (data.data && Array.isArray(data.data)) {
        teams = data.data
      } else if (data.teams && Array.isArray(data.teams)) {
        teams = data.teams
      } else {
        result.errors.push({
          index: -1,
          message: 'Invalid import format. Expected array of teams or object with "data" or "teams" field.',
        })
        return result
      }

      teams.forEach((team, index) => {
        const validation = this.validateTeam(team, index)

        if (!validation.valid) {
          result.errors.push(...validation.errors)
          result.skipped++

          if (!options.skipInvalid) {
            return
          }
        } else {
          const teamCreate: AgentTeamCreate = {
            name: team.name,
            description: team.description,
            strategy: team.strategy,
            members: team.members || [],
            feedback_enabled: team.feedback_enabled || false,
            max_iterations: team.max_iterations,
          }

          result.data!.push(teamCreate)
          result.imported++
        }

        if (validation.warnings.length > 0) {
          result.warnings.push(...validation.warnings)
        }
      })

      result.success = result.errors.length === 0 || options.skipInvalid === true

      return result
    } catch (error: any) {
      result.errors.push({
        index: -1,
        message: error.message || 'Import failed',
      })
      return result
    }
  }

  /**
   * Import workflows from JSON
   */
  async importWorkflows(
    data: any,
    options: ImportOptions = {}
  ): Promise<ImportResult<WorkflowCreate>> {
    const result: ImportResult<WorkflowCreate> = {
      success: false,
      data: [],
      errors: [],
      warnings: [],
      imported: 0,
      skipped: 0,
    }

    try {
      let workflows: any[] = []

      if (Array.isArray(data)) {
        workflows = data
      } else if (data.data && Array.isArray(data.data)) {
        workflows = data.data
      } else if (data.workflows && Array.isArray(data.workflows)) {
        workflows = data.workflows
      } else {
        result.errors.push({
          index: -1,
          message: 'Invalid import format. Expected array of workflows or object with "data" or "workflows" field.',
        })
        return result
      }

      workflows.forEach((workflow, index) => {
        const validation = this.validateWorkflow(workflow, index)

        if (!validation.valid) {
          result.errors.push(...validation.errors)
          result.skipped++

          if (!options.skipInvalid) {
            return
          }
        } else {
          const workflowCreate: WorkflowCreate = {
            name: workflow.name,
            description: workflow.description,
            framework: workflow.framework || 'langchain',
            definition: workflow.definition,
            status: workflow.status || 'draft',
          }

          result.data!.push(workflowCreate)
          result.imported++
        }

        if (validation.warnings.length > 0) {
          result.warnings.push(...validation.warnings)
        }
      })

      result.success = result.errors.length === 0 || options.skipInvalid === true

      return result
    } catch (error: any) {
      result.errors.push({
        index: -1,
        message: error.message || 'Import failed',
      })
      return result
    }
  }

  /**
   * Validate agent data
   */
  private validateAgent(agent: any, index: number): {
    valid: boolean
    errors: ImportError[]
    warnings: string[]
  } {
    const errors: ImportError[] = []
    const warnings: string[] = []

    if (!agent.name || typeof agent.name !== 'string') {
      errors.push({
        index,
        field: 'name',
        message: `Agent ${index + 1}: Name is required`,
      })
    }

    if (!agent.framework || !['langchain', 'crewai', 'adk', 'custom'].includes(agent.framework)) {
      errors.push({
        index,
        field: 'framework',
        message: `Agent ${index + 1}: Invalid framework. Must be one of: langchain, crewai, adk, custom`,
      })
    }

    if (agent.temperature !== undefined) {
      if (typeof agent.temperature !== 'number' || agent.temperature < 0 || agent.temperature > 2) {
        errors.push({
          index,
          field: 'temperature',
          message: `Agent ${index + 1}: Temperature must be between 0 and 2`,
        })
      }
    }

    if (agent.max_tokens !== undefined && (typeof agent.max_tokens !== 'number' || agent.max_tokens < 1)) {
      errors.push({
        index,
        field: 'max_tokens',
        message: `Agent ${index + 1}: max_tokens must be a positive number`,
      })
    }

    if (!agent.model) {
      warnings.push(`Agent ${index + 1}: No model specified, will use default`)
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * Validate team data
   */
  private validateTeam(team: any, index: number): {
    valid: boolean
    errors: ImportError[]
    warnings: string[]
  } {
    const errors: ImportError[] = []
    const warnings: string[] = []

    if (!team.name || typeof team.name !== 'string') {
      errors.push({
        index,
        field: 'name',
        message: `Team ${index + 1}: Name is required`,
      })
    }

    if (!team.strategy || !['sequential', 'parallel', 'hierarchical', 'consensus', 'debate'].includes(team.strategy)) {
      errors.push({
        index,
        field: 'strategy',
        message: `Team ${index + 1}: Invalid strategy`,
      })
    }

    if (!team.members || !Array.isArray(team.members) || team.members.length === 0) {
      errors.push({
        index,
        field: 'members',
        message: `Team ${index + 1}: At least one member is required`,
      })
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    }
  }

  /**
   * Validate workflow data
   */
  private validateWorkflow(workflow: any, index: number): {
    valid: boolean
    errors: ImportError[]
    warnings: string[]
  } {
    const errors: ImportError[] = []
    const warnings: string[] = []

    if (!workflow.name || typeof workflow.name !== 'string') {
      errors.push({
        index,
        field: 'name',
        message: `Workflow ${index + 1}: Name is required`,
      })
    }

    if (!workflow.definition || typeof workflow.definition !== 'object') {
      errors.push({
        index,
        field: 'definition',
        message: `Workflow ${index + 1}: Definition is required and must be an object`,
      })
    } else {
      if (!workflow.definition.nodes || !Array.isArray(workflow.definition.nodes)) {
        errors.push({
          index,
          field: 'definition.nodes',
          message: `Workflow ${index + 1}: Definition must have nodes array`,
        })
      }

      if (!workflow.definition.edges || !Array.isArray(workflow.definition.edges)) {
        errors.push({
          index,
          field: 'definition.edges',
          message: `Workflow ${index + 1}: Definition must have edges array`,
        })
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    }
  }
}

export const importService = new ImportService()
export default importService
